import { Trash2, Plus } from "@/components/icons";
import type { RateRule, FeeType, Uom } from "@/lib/api";
import {
  type PricingFormValues, UOM_BY_FEE, UOM_RATE_LABEL, UOM_UNIT_LABEL, newRule,
} from "@/lib/pricingForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]; // Sun=0 .. Sat=6

function daysToSet(csv?: string): Set<number> {
  if (!csv) return new Set();
  return new Set(csv.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n)));
}
function setToDays(s: Set<number>): string {
  return [...s].sort((a, b) => a - b).join(",");
}

/**
 * The ONE shared pricing-rule editor. Controlled (value + onChange). Used identically by
 * the charger default-tariff editor and the Pricing Profiles create/edit form — same
 * fields (currency; per-rule fee type charging/idle/activation, unit, rate, optional
 * time-of-use window with days + priority), same validation/serialization (lib/pricingForm).
 * The parent owns saving; this component renders nothing about WHERE it is saved.
 */
export function PricingForm({
  value, onChange, idPrefix = "pricing",
}: {
  value: PricingFormValues;
  onChange: (v: PricingFormValues) => void;
  idPrefix?: string;
}) {
  const { rules, currency } = value;
  const setRules = (rules: RateRule[]) => onChange({ ...value, rules });

  const updateRule = (i: number, patch: Partial<RateRule>) =>
    setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const changeFeeType = (i: number, fee: FeeType) =>
    updateRule(i, { fee_type: fee, uom: UOM_BY_FEE[fee][0] });
  const addRule = () => setRules([...rules, newRule()]);
  const removeRule = (i: number) => setRules(rules.filter((_, idx) => idx !== i));
  const toggleDay = (i: number, day: number) => {
    const s = daysToSet(rules[i].days);
    if (s.has(day)) s.delete(day); else s.add(day);
    updateRule(i, { days: setToDays(s) });
  };
  const applyFreeWindowPreset = () =>
    setRules([
      { fee_type: "charging", uom: "kwh", rate_base: 0.3, is_tou: false, start_time: "", end_time: "", days: "", priority: 0 },
      { fee_type: "charging", uom: "kwh", rate_base: 0, is_tou: true, start_time: "09:00", end_time: "18:00", days: "", priority: 10 },
    ]);

  const isFree = !rules.some((r) => (Number(r.rate_base) || 0) > 0);

  return (
    <div className="space-y-3" data-testid="pricing-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label className="text-xs">Currency</Label>
          <Input className="mt-1 w-24" data-testid="pricing-currency" value={currency}
            onChange={(e) => onChange({ ...value, currency: e.target.value })} placeholder="USD" />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isFree ? "success" : "neutral"} className="h-6 px-2.5 text-sm">
            {isFree ? "FREE" : "PAID"}
          </Badge>
          <Button type="button" variant="ghost" size="sm" onClick={applyFreeWindowPreset}>
            Preset: free 09:00–18:00
          </Button>
        </div>
      </div>

      {rules.length === 0 && (
        <div className="text-sm opacity-60" data-testid="pricing-empty">
          No rules — free for everyone. Add a rule to charge for energy, idle time or activation,
          optionally limited to a time window.
        </div>
      )}

      <div className="space-y-3">
        {rules.map((rule, i) => {
          const uoms = UOM_BY_FEE[rule.fee_type];
          const daySet = daysToSet(rule.days);
          return (
            <div key={i} className="rounded-md border p-3 space-y-3" data-testid={`pricing-rule-${i}`}>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs">Fee type</Label>
                  <Select value={rule.fee_type} onValueChange={(v) => changeFeeType(i, v as FeeType)}>
                    <SelectTrigger className="mt-1 w-32" data-testid={`pricing-feetype-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="charging">Charging</SelectItem>
                      <SelectItem value="idle">Idle</SelectItem>
                      <SelectItem value="activation">Activation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Unit</Label>
                  <Select value={rule.uom} onValueChange={(v) => updateRule(i, { uom: v as Uom })}
                    disabled={uoms.length === 1}>
                    <SelectTrigger className="mt-1 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {uoms.map((u) => <SelectItem key={u} value={u}>{UOM_UNIT_LABEL[u]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Rate ({rule.uom === "session" ? "$" : UOM_RATE_LABEL[rule.uom]})</Label>
                  <Input
                    className={`mt-1 w-28 ${Number(rule.rate_base) === 0 ? "placeholder:text-emerald-600 placeholder:font-medium" : ""}`}
                    data-testid={`pricing-rate-${i}`}
                    type="number" step="0.01" min="0" placeholder="Free"
                    value={Number(rule.rate_base) === 0 ? "" : rule.rate_base}
                    onChange={(e) => updateRule(i, { rate_base: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={!!rule.is_tou} onCheckedChange={(c) => updateRule(i, { is_tou: c })} />
                  <Label className="text-xs">Time window</Label>
                </div>
                <Button type="button" variant="ghost" size="icon" className="ml-auto" onClick={() => removeRule(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {rule.is_tou && (
                <div className="flex flex-wrap items-end gap-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.start_time || ""}
                      onChange={(e) => updateRule(i, { start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.end_time || ""}
                      onChange={(e) => updateRule(i, { end_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Days (empty = all)</Label>
                    <div className="mt-1 flex gap-1">
                      {DAY_LABELS.map((lbl, d) => (
                        <button key={d} type="button" onClick={() => toggleDay(i, d)}
                          className={`h-7 w-7 rounded text-xs border ${daySet.has(d) ? "bg-primary text-primary-foreground" : "bg-transparent opacity-70"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Input className="mt-1 w-20" type="number" step="1" value={rule.priority ?? 0}
                      onChange={(e) => updateRule(i, { priority: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRule} data-testid="pricing-add-rule">
        <Plus className="h-4 w-4 mr-1" /> Add rule
      </Button>
      <p className="text-xs opacity-50">
        A time-window rule applies only inside its window; it overrides the all-day rule for the same
        fee. Outside every window, the all-day rule (if any) applies — otherwise it's free.
      </p>
    </div>
  );
}
